import { ReactNode, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth/authContext';

interface AuthGuardProps {
  children: ReactNode;
  fallbackPath?: string;
}

export default function AuthGuard({ 
  children, 
  fallbackPath = '/login'
}: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      router.push(fallbackPath);
    }
  }, [isAuthenticated, isLoading, router, fallbackPath]);

  // Show loading indicator
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
        <div className="animate-pulse flex space-x-2 mb-4">
          <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
          <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
          <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
        </div>
        <p className="text-zinc-500 dark:text-zinc-400">
          Verifying your access...
        </p>
      </div>
    );
  }

  // Show auth required message
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
            Authentication Required
          </h2>
          <p className="text-zinc-500 dark:text-zinc-400 max-w-md">
            Please sign in to access this content. You will be redirected to the login page.
          </p>
        </div>
        <div className="animate-pulse flex space-x-2">
          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
} 